import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useAuth, useResource, useGrid, useStore, useToast, usePage, useModal } from "@/comn/hooks"; // hook
import { URLS, APIS, BASE, SG_NRCPT_LIST, SF_NRCPT_LIST_SRCH } from "./services/OsciRcptService"; // 서비스
import dayjs from "dayjs";

/*
 * Non-Receipt List
 * !== 미수납 목록 ==!
 */
export const OSCI0402003Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0059" },
            { area: "comnCd", comnCd: "OSC0063" },
            { area: "coCd" },
            { area: "cstmCd" },
        ],
    });

    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003", "auth_osci0021"],
    }); // Page Unique identifier !== 화면 고유 식별자 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    let pgeTitle = "";

    if (menuId?.includes("menu_coli")) {
        pgeTitle = "T_NRCPT_NTFC_LST";
    } else if (menuId?.includes("menu_olmi")) {
        pgeTitle = "T_OIL_NRCPT_NTFC_LST";
    }

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Receipt Search !== 수납 검색 ==!
        nrcptSrch: useForm({
            defaultSchema: SF_NRCPT_LIST_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Receipt List !== 수납 목록 ==!
        nrcptList: useGrid({
            defaultSchema: SG_NRCPT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get Non-Receipt List !== 미수납 목록 조회 ==!
        getNrcptList: useFetch({
            api: (page = grid.nrcptList.page) => {
                if (menuId?.includes("olmi")) {
                    form.nrcptSrch.setValue("oilDclrYn", "Y");
                }
                let data = {
                    ...form.nrcptSrch.getValues(),
                    orgCd: auth.get("orgCd"),
                };
                if (data.bllNo) {
                    data.bllNo = data.bllNo.trim(); // 앞뒤 공백 제거
                    // Bill No.가 "X-XXXX-XX-XXXXXXX-X"(bllMgmtId 마스크) 패턴이면 '-' 제거 후 조회
                    if (/^[A-Za-z0-9]-[A-Za-z0-9]{4}-[A-Za-z0-9]{2}-[A-Za-z0-9]{7}-[A-Za-z0-9]$/.test(data.bllNo)) {
                        data.bllNo = data.bllNo.replaceAll("-", "");
                    }
                }
                return APIS.getNrcptList(data, page, grid.nrcptList.size);
            },
            enabled: comnUtils.isEmpty(form.nrcptSrch.errors) && form.nrcptSrch.isSubmitted,
            key: [grid.nrcptList.page, grid.nrcptList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.nrcptSrch.getValues(),
                    page: grid.nrcptList.page,
                    size: grid.nrcptList.size,
                });
            },
            onError: () => {},
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Get Non-Receipt List !== 미수납 목록 조회 ==!
        getNrcptList: (page: number = 0) => {
            // Validation Check !== 밸리데이션 체크 ==!
            if (!dateValidate()) {
                return false;
            }

            form.nrcptSrch.handleSubmit(
                () => {
                    // Validation Check !== 밸리데이션 체크 ==!
                    grid.nrcptList.setPage(page);
                    fetch.getNrcptList.fetch(page);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
    };

    const dateValidate = () => {
        const srchData = form.nrcptSrch.getValues();
        let result: boolean = true;

        // 고지일자: 최대 1년 (윤년이 끼어도 같은 날짜까지 허용하도록 일수 대신 1년 뒤 날짜와 비교)
        if (dayjs(srchData.bllEndDt).isAfter(dayjs(srchData.bllStrtDt).add(1, "year"))) {
            modal.openModal({
                content: t("msg.osc.00336", { 0: t("L_BLL_DT") }), // For Bill Date, the search period cannot exceed 1 year.
            });
            result = false;
        }
        return result;
    };

    const render = {
        grid_nrcptList: {
            head: {},
            cell: {
                bllNo: (props: any) => {
                    const { rowValues } = props;
                    return (
                        <Layout direction="col" gap={1}>
                            <span style={{ color: "blue" }}>
                                <Link
                                    to={`${URLS.osci0402002q}/${rowValues.bllTpCd}-${rowValues.bllSysmCd}-${rowValues.bllYy}-${rowValues.bllSrno}-${rowValues.bllDgr}/collection/OSCI0402003Q`}
                                >{`${rowValues.bllNo}`}</Link>
                            </span>
                        </Layout>
                    );
                },
                tansadNo: (props: any) => {
                    const { rowValues } = props;

                    if (rowValues.reffKeyTpCd == "T") {
                        return (
                            <Layout direction="col" gap={1}>
                                <span style={{ color: "blue" }}>
                                    <Link
                                        to={`${URLS.osci0401003q}/${rowValues.reffKeyNo1}-${rowValues.reffKeyNo2}-${rowValues.reffKeyNo3}/OSCI0402003Q`}
                                    >{`${rowValues.reffKeyNo1}-${rowValues.reffKeyNo2}-${rowValues.reffKeyNo3}`}</Link>
                                </span>
                            </Layout>
                        );
                    } else if (rowValues.tansadNo != null && rowValues.tansadNo.length > 6) {
                        const reffKeyNo1 = rowValues.tansadNo.substring(0, 4);
                        const reffKeyNo2 = rowValues.tansadNo.substring(4, 6);
                        const reffKeyNo3 = rowValues.tansadNo.substring(6);

                        console.log(`${reffKeyNo1}-${reffKeyNo2}-${reffKeyNo3}`);
                        return (
                            <Layout direction="col" gap={1}>
                                <span style={{ color: "blue" }}>
                                    <Link
                                        to={`${URLS.osci0401003q}/${reffKeyNo1}-${reffKeyNo2}-${reffKeyNo3}`}
                                    >{`${reffKeyNo1}-${reffKeyNo2}-${reffKeyNo3}`}</Link>
                                </span>
                            </Layout>
                        );
                    }
                },
            },
            edit: {},
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        // handler.getNrcptList(pgeStore?.page);
    }, []);

    return (
        <Page title={t(pgeTitle)}>
            <form>
                <Group>
                    <Group.Body>
                        <Layout direction="row">
                            <Layout.Left size={6}>{}</Layout.Left>
                            <Layout.Right>{}</Layout.Right>
                        </Layout>
                        <Group.Section>
                            <Group.Row>
                                {/* Receipt Custom Office Code !== 고지서 세관 코드 ==!  */}
                                <Group.Control {...form.nrcptSrch.schema.cstmOfceCd}></Group.Control>
                                {/* TANSAD Number !== TANSAD 번호 ==!  */}
                                <Group.Control {...form.nrcptSrch.schema.tansadNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* Declarant TIN !== 신고인 TIN ==!  */}
                                <Group.Control {...form.nrcptSrch.schema.dcltTin}></Group.Control>
                                <Group.Control {...form.nrcptSrch.schema.bllNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* Deadline Date !== 데드라인 일자 ==!  */}
                                <Group.Control {...form.nrcptSrch.schema.ddlnDtmRnge}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* Bill Date !== 고지서 일자 ==!  */}
                                <Group.Control {...form.nrcptSrch.schema.bllDtmRnge}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* GePG Control Number !== GePG 컨트롤 번호 ==!  */}
                                <Group.Control {...form.nrcptSrch.schema.ctrlNo}></Group.Control>
                                {/* Control Number Issued !== 컨트롤번호 발급 여부 ==!  */}
                                <Group.Control {...form.nrcptSrch.schema.ctrlNoYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                {/* State !== 상태 ==!  */}
                                <Group.Control {...form.nrcptSrch.schema.dlqnYn}></Group.Control>
                            </Group.Row>
                        </Group.Section>

                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role="reset"
                                    onClick={() => {
                                        form.nrcptSrch.reset();
                                    }}
                                ></Button>
                            </Layout.Left>
                            <Layout.Right>
                                <Button
                                    role="search"
                                    onClick={() => {
                                        handler.getNrcptList();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>

            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    grid.nrcptList.exportExcel({
                                        /* 다운로드 엑셀 파일명 (확장자 없이), 기본값 Export(년월일시분초) */
                                        excelName: dayjs().format("YYYY-MM-DD HH:mm:ss") + "_" + pgeUid,
                                        /* 다운로드 엑셀 시트명 , 기본값 Sheet */
                                        sheetName: "Sheet Name",
                                    });
                                }}
                            ></Button>
                            <Button
                                onClick={() => {
                                    form.nrcptSrch.handleSubmit(
                                        () => {
                                            // Validation Check !== 밸리데이션 체크 ==!
                                            if (!dateValidate()) {
                                                return false;
                                            }
                                            comnUtils.exportExcel({
                                                url: `${BASE.api}/nrcptExc`,
                                                data: { payYn: "N", ...form.nrcptSrch.getValues() },
                                                fileName: "Notice Non-Receipt List",
                                            });
                                        },
                                        () => {
                                            toast.showToast({ type: "warning", content: "msg.osc.00003" });
                                        },
                                    )();
                                }}
                            >
                                {t("B_DTL_DWNL")}
                            </Button>
                        </Layout.Left>
                    </Layout>
                    <Grid
                        {...grid.nrcptList.grid}
                        data={fetch.getNrcptList.data?.nrcptList}
                        render={render.grid_nrcptList}
                    />
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left></Layout.Left>
            </Layout>
        </Page>
    );
};
